import { useCallback, useEffect, useState } from "react";
import { Crown, ShieldCheck } from "lucide-react";
import { MembersPanel } from "./MembersPanel";
import { RoleManager } from "./RoleManager";
import { PermissionMatrix } from "./PermissionMatrix";
import { OverrideEditor } from "./OverrideEditor";
import { AccessLog } from "./AccessLog";
import { PioneerMark } from "./shared";

function usePioneers(api) {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => api.get("/admin/pioneers/overview").then((r) => { setData(r.data); setTick((t) => t + 1); }).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  return { data, reload: load, tick };
}

function Hierarchy() {
  const rows = [
    ["Admin (platform)", "Unrestricted, platform-wide. Cannot be limited."],
    ["Dinesh Singh — Executive Director", "Hardcoded to his profile: access identical to Admin."],
    ["Executive Director (others)", "Full access to VCB and Banking only; everything else read-only."],
    ["Founder / Co-Founder", "Selected modules, read-only. No initiation or approval."],
    ["Chairman", "Read across all modules; approves but never initiates."],
    ["Non-Executive Director", "Read-only everywhere. No edit or approval rights."],
    ["Board Member", "Reports and ledgers only. No Finance, VCB or Banking."],
    ["Advisory Board Member", "Dashboard and Operations Map only."],
  ];
  return (
    <div className="admin-card" data-testid="pioneer-hierarchy">
      <div className="flex items-center gap-2 mb-4"><ShieldCheck size={16} className="text-[#C9A84C]" /><span className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A84C]">Default permissions hierarchy</span></div>
      <ol className="space-y-2">
        {rows.map(([t, d], i) => (
          <li key={t} className="grid grid-cols-[28px_1fr] gap-3 text-sm">
            <span className="font-mono text-[#C9A84C]">{String(i + 1).padStart(2, "0")}</span>
            <span><span className="text-[#F5F5F5] font-medium">{t}</span><span className="text-[#888888]"> — {d}</span></span>
          </li>
        ))}
      </ol>
    </div>
  );
}

const Loading = () => <div className="admin-card text-sm text-[#888888]" data-testid="pioneers-loading">Loading The Pioneers…</div>;

export function PioneersPanel({ api }) {
  const { data, reload } = usePioneers(api);
  if (!data) return <Loading />;
  return (
    <div className="space-y-6" data-testid="pioneers-panel">
      <div className="flex items-center gap-3"><Crown size={20} className="text-[#C9A84C]" /><PioneerMark /><span className="text-xs text-[#888888]">· {data.members.length} members · {data.roles.length} sub-roles · visible to Admin only</span></div>
      <MembersPanel data={data} api={api} onChanged={reload} />
      <Hierarchy />
    </div>
  );
}

export function PioneerAccessControl({ api }) {
  const { data, reload, tick } = usePioneers(api);
  const [selectedId, setSelectedId] = useState(null);
  if (!data) return <Loading />;
  const selected = data.members.find((m) => m.id === selectedId) || null;
  return (
    <div className="space-y-6" data-testid="pioneer-access-control">
      <div className="flex items-center gap-3"><Crown size={20} className="text-[#C9A84C]" /><PioneerMark /><span className="text-xs text-[#888888]">· Pioneer Access Control · changes apply in real time and are logged</span></div>
      <RoleManager data={data} api={api} onChanged={reload} />
      <PermissionMatrix data={data} api={api} onChanged={reload} />
      <MembersPanel data={data} api={api} onChanged={reload} compact onSelect={(m) => setSelectedId(m.id)} selectedId={selectedId} />
      <OverrideEditor data={data} api={api} member={selected} onChanged={reload} onPick={(m) => setSelectedId(m?.id || null)} />
      <AccessLog api={api} refreshKey={tick} />
    </div>
  );
}
